// src/context/SelectedVideosContext.js
import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { boxFetcher } from "../api/requests";

const SelectedVideosContext = createContext();

export const SelectedVideosProvider = ({ children }) => {
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [videoBoxRelationIds, setVideoBoxRelationIds] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);
  const [stateLoading, setStateLoading] = useState({});
  const [videoType, setVideoType] = useState("");

  const setVideoLoading = (videoId, isLoading) => {
    setStateLoading((prev) => ({ ...prev, [videoId]: isLoading }));
  };

  const updateVideoBoxRelationIds = useCallback((values) => {
    setVideoBoxRelationIds(values);
  }, []);

  const isVideoLoading = useCallback(
    (id) => {
      return !!stateLoading[id];
    },
    [stateLoading]
  );

  const addVideo = useCallback(
    async (videoId) => {
      // Check if video is already selected before making API call
      const isAlreadySelected = selectedVideos.includes(videoId);
      
      setSelectedVideos((prev) =>
        prev.includes(videoId) ? prev : [...prev, videoId]
      );
      
      // Only make POST request if video wasn't already selected and box is defined
      if (!isAlreadySelected && selectedBox) {
        try {
          setVideoLoading(videoId, true);
          let response;
          if (videoType === "campaign") {
            response = await boxFetcher.boxItemByHandForCampaign({
              box: selectedBox,
              video: videoId,
            });
          } else {
            response = await boxFetcher.boxItemByHand({
              box: selectedBox,
              video: videoId,
            });
          }
          
          // Update videoBoxRelationIds with the new relationId from response
          if (response?.id) {
            setVideoBoxRelationIds((prev) => {
              // Remove old entry if exists, then add new one
              const filtered = prev ? prev.filter(item => item.videoId !== videoId) : [];
              return [...filtered, { videoId: videoId, relationId: response.id }];
            });
          }
        } catch (err) {
          console.error(err);
        } finally {
          setVideoLoading(videoId, false);
        }
      }
    },
    [selectedBox, selectedVideos, videoType]
  );

  const removeVideo = useCallback(
    async (id) => {
      setSelectedVideos((prev) => prev.filter((videoId) => videoId !== id));
      try {
        setVideoLoading(id, true);
        const chosenVideoToDelete = videoBoxRelationIds?.find(
          (item) => item.videoId === id
        );

        if (chosenVideoToDelete?.relationId) {
          if (videoType === "campaign") {
            await boxFetcher.deleteVideoByHandForCampaign(
              chosenVideoToDelete.relationId
            );
          } else {
            await boxFetcher.deleteVideoByHand(
              chosenVideoToDelete.relationId
            );
          }
          
          // Remove from videoBoxRelationIds after successful deletion
          setVideoBoxRelationIds((prev) => 
            prev ? prev.filter(item => item.videoId !== id) : []
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setVideoLoading(id, false);
      }
    },
    [videoBoxRelationIds, videoType]
  );

  const isVideoSelected = useCallback(
    (id) => selectedVideos.includes(id),
    [selectedVideos]
  );

  const resetArray = useCallback(() => {
    setSelectedVideos([]);
    setVideoBoxRelationIds([]);
  }, []);

  // Initialize selected videos without making POST requests (used when loading existing selections)
  const initializeSelectedVideos = useCallback((videoIds) => {
    setSelectedVideos(videoIds);
  }, []);

  const contextValue = useMemo(
    () => ({
      selectedVideos,
      addVideo,
      removeVideo,
      isVideoSelected,
      resetArray,
      updateVideoBoxRelationIds,
      setSelectedBox,
      isVideoLoading,
      setVideoType,
      initializeSelectedVideos,
    }),
    [
      selectedVideos,
      addVideo,
      removeVideo,
      isVideoSelected,
      resetArray,
      updateVideoBoxRelationIds,
      isVideoLoading,
      setVideoType,
      initializeSelectedVideos,
    ]
  );

  return (
    <SelectedVideosContext.Provider value={contextValue}>
      {children}
    </SelectedVideosContext.Provider>
  );
};

export const useSelectedVideos = () => useContext(SelectedVideosContext);
